import { Status } from '../../common/domain/types';
export type GenPoint = { timestamp: string; value: number | null };

export interface ModelInputs {
  customer_id: string;
  project_name: string;
  latitude: number | "";
  longitude: number | "";
  state: string;
  county: string;
  city: string;
  zip_code: string;
  timezone: string;
  project_nerc_code: string;
  eia_ba_code: string;
  cod_date: string | "";
  decommision_date: string | "";
  nameplate_capacity: number | "";
  poi_limit: number | "";
  project_additional_load: number | "";
  ppa_percentage: number | "";
  ppa_capacity: number | "";
  battery_capacity_mwh: number | "";
  battery_charge_rate_mw: number | "";
  battery_discharge_rate_mw: number | "";
  battery_round_trip_eff: number | "";
  battery_soc_min: number | "";
  battery_chemistry: string;
  battery_calendar_life_years: number | "";
  battery_cycle_life: number | "";
  battery_augmentation_flag: boolean;
  battery_depth_of_discharge: number | "";
  existing_project_flag: boolean;
  sam_profile_flag?: boolean;
  ppa_flag: boolean;
  battery_flag: boolean;
  tech_type_id: string;
  gen_hour_profile_mw: GenPoint[];
}

// Project domain types
export interface Project {
  id: string;
  project_id?: string; // Added for compatibility with API responses
  project_name: string;
  customer_id: string;
  status: Status;
  created_at: string;
  updated_at: string;
  model_inputs: Partial<ModelInputs>;
  latest_run_id?: string;
}

export interface ProjectDetails extends ModelInputs {
  id: string;
  created_at: string;
  updated_at: string;
  status: Status;
  customer_name?: string;
  runs?: {
    id: string;
    status: string;
    created_at: string;
    completed_at?: string;
  }[];
}

export interface ProjectTile {
  project_id: string;  // Use project_id instead of id
  name: string;
  status: Status;
  config_version?: number; // Add this field from the API response
  portfolio_id?: string;
  tech_type?: string;
  mw_capacity?: number;
  eia_ba_code?: string;
  // Latest run + COD context for table columns / sub-labels.
  cod_date?: string | null;
  decommision_date?: string | null;
  latest_run_status?: string | null;
  latest_run_id?: string | null;
  latest_run_start_date?: string | null;
  latest_run_end_date?: string | null;
}

export interface ProjectWithImpact extends ProjectTile {
  impact?: {
    dalys: number;
    pdfs: number;
    economic: number;
    mwCapacity: number;
  }
}

export interface ProjectData {
  project_id: string;
  name: string;
  status: Status;
}

export interface ProjectsResponse {
  projects: ProjectData[] | ProjectTile[];
}

// Customer related types
export interface Contact {
  email: string;
}

export interface Customer {
  customer_id: string;
  customer_name: string;
}

export interface CustomerStatus {
  customer_id: string;
  plan_type: string;
  gating_disabled: boolean;
  monthly_run_limit: number;
  monthly_mw_limit: number;
  runs_used: number;
  runs_remaining: number;
  mw_used: number;
  mw_remaining: number;
}

/** Key groups (single source of truth) */
export const textKeys = [
  "project_name",
  "state",
  "county",
  "city",
  "zip_code",
  "timezone",
  "project_nerc_code",
  "eia_ba_code",
  "tech_type_id",
  "battery_chemistry",
] as const;

export const numberKeys = [
  "latitude",
  "longitude",
  "nameplate_capacity",
  "poi_limit",
  "project_additional_load",
  "ppa_percentage",
  "ppa_capacity",
  "battery_capacity_mwh",
  "battery_charge_rate_mw",
  "battery_discharge_rate_mw",
  "battery_round_trip_eff",
  "battery_soc_min",
  "battery_calendar_life_years",
  "battery_cycle_life",
  "battery_depth_of_discharge",
] as const;

export const dateKeys = ["cod_date", "decommision_date"] as const;
export const boolKeys = ["ppa_flag", "battery_flag", "battery_augmentation_flag"] as const;

export const ENTRY_KEYS = [...textKeys, ...numberKeys, ...dateKeys] as const;
export type EntryKey = typeof ENTRY_KEYS[number];

export const GRID = "grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5";

// ============================================================
// API Response Types (match backend schemas exactly)
// These represent the raw structure returned by the API
// ============================================================

/**
 * Raw API response from GET /projects/{id}
 * Backend schema: ProjectDetailOut
 */
export interface ProjectDetailApiResponse {
  project_id: string;
  config: Partial<ModelInputs> & { project_name?: string };
  config_version?: number;
  latest_run?: Record<string, unknown> | null;
  latest_run_status?: string | null;
  latest_run_id?: string | null;
}

/**
 * Raw API response from PATCH /projects/{id}
 * Backend schema: ProjectPatchOut
 */
export interface ProjectPatchApiResponse {
  project_id: string;
  config: Partial<ModelInputs>;
  config_version?: number;
  queued_model_run_id?: string | null;
}

// ============================================================
// Flattened Types (what components receive after transformation)
// ============================================================

/**
 * Flattened project details for component consumption.
 * Merges config fields to top level for easier access.
 */
export interface ProjectDetail extends Partial<ModelInputs> {
  project_id: string;
  project_name: string;
  config_version?: number;
  latest_run?: Record<string, unknown> | null;
  latest_run_status?: string | null;
  latest_run_id?: string | null;
}

// ============================================================
// API Response Transformers
// Transform nested API responses to flat component-friendly format
// ============================================================

/**
 * Flatten ProjectDetailApiResponse -> ProjectDetail
 * Single source of truth for this transformation.
 */
export function flattenProjectDetail(response: ProjectDetailApiResponse): ProjectDetail {
  return {
    project_id: response.project_id,
    config_version: response.config_version,
    latest_run: response.latest_run,
    latest_run_status: response.latest_run_status,
    latest_run_id: response.latest_run_id,
    // Spread config fields to top level
    ...response.config,
    // Ensure project_name is always present
    project_name: response.config.project_name || '',
  };
}

/**
 * Flatten ProjectPatchApiResponse -> partial ProjectDetail
 */
export function flattenProjectPatch(response: ProjectPatchApiResponse): Partial<ProjectDetail> & { project_id: string; queued_model_run_id?: string | null } {
  return {
    project_id: response.project_id,
    config_version: response.config_version,
    queued_model_run_id: response.queued_model_run_id,
    ...response.config,
  };
}
